import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { packListsApi, marketDaysApi } from '../../services/api';
import './PackListDetail.css';

export default function PackListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packList, setPackList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [marketDays, setMarketDays] = useState([]);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [plData, itemData, mdData] = await Promise.all([
        packListsApi.get(id),
        packListsApi.getItems(id),
        marketDaysApi.getAll(),
      ]);
      setPackList(plData.pack_list);
      setItems(itemData.items || []);
      setMarketDays(mdData.market_days || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function handleLocalChange(itemId, field, value) {
    setItems(is => is.map(i => i.id === itemId ? { ...i, [field]: value } : i));
  }

  async function handleBlur(itemId, field, value) {
    try {
      await packListsApi.updateItem({ id: itemId, [field]: value });
    } catch (e) {
      setError(e.message);
    }
  }

  async function handlePhotoScan(e) {
    const file = e.target.files[0];
    if (!file) return;
    setScanning(true);
    setError('');
    try {
      const base64 = await fileToBase64(file);
      const result = await packListsApi.scanActuals({ image: base64, packListId: id });
      if (result.items) {
        setItems(is => {
          const updated = [...is];
          result.items.forEach(scanned => {
            const idx = updated.findIndex(i => i.id === scanned.id);
            if (idx >= 0) updated[idx] = { ...updated[idx], ...scanned };
          });
          return updated;
        });
      }
    } catch (e) {
      setError('Scan failed: ' + e.message);
    } finally {
      setScanning(false);
      fileRef.current.value = '';
    }
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handlePushToMarketDay() {
    if (!packList?.market_id) {
      alert('This pack list has no market assigned.');
      return;
    }
    setPushing(true);
    try {
      // Find market days for this market
      const matchingDays = marketDays.filter(md =>
        String(md.market_id) === String(packList.market_id)
      );

      let targetDay;
      if (matchingDays.length === 0) {
        // Auto-create the market day
        const result = await marketDaysApi.create({
          market_id: packList.market_id,
          date: String(packList.week_date).slice(0, 10),
          weather_condition: 'Sunny',
          temperature: '',
          notes: 'Auto-created from pack list',
        });
        targetDay = { id: result.id, date: packList.week_date };
        // Refresh market days
        const mdData = await marketDaysApi.getAll();
        setMarketDays(mdData.market_days || []);
      } else if (matchingDays.length === 1) {
        targetDay = matchingDays[0];
      } else {
        const options = matchingDays.map((md, i) => (i + 1) + ': ' + md.date).join('\n');
        const choice = window.prompt('Multiple market days found. Enter number:\n' + options);
        if (!choice) { setPushing(false); return; }
        targetDay = matchingDays[parseInt(choice) - 1];
      }

      if (!targetDay) { setPushing(false); return; }

      const res = await packListsApi.pushPackedToMarketDay(id, targetDay.id);
      if (res.success) {
        alert('✓ Packed quantities pushed to market day for ' + packList.market_name + ' on ' + targetDay.date);
      } else {
        alert('Error: ' + res.error);
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setPushing(false);
    }
  }

  function handlePrint() {
    window.print();
  }

  async function markPrinted() {
    try {
      await packListsApi.update({ id, status: 'printed' });
      setPackList(pl => ({ ...pl, status: 'printed' }));
    } catch (e) {
      setError(e.message);
    }
  }

  async function markComplete() {
    try {
      await packListsApi.update({ id, status: 'complete' });
      setPackList(pl => ({ ...pl, status: 'complete' }));
    } catch (e) {
      setError(e.message);
    }
  }

  // Group items by market for AR, or by category for CC
  function groupItems() {
    if (!packList) return [];
    if (packList.type === 'AR') {
      const markets = [...new Set(items.map(i => i.market_name))];
      return markets.map(market => ({
        label: market,
        items: items.filter(i => i.market_name === market),
      }));
    } else {
      const categories = [...new Set(items.map(i => i.category))];
      return categories.map(cat => ({
        label: cat,
        items: items.filter(i => i.category === cat),
      }));
    }
  }

  const groups = groupItems();

  if (loading) return <div className="loading-state" style={{padding: 48}}>Loading...</div>;
  if (!packList) return <div style={{padding: 48}}>Pack list not found.</div>;

  return (
    <div className="page">
      <div className="detail-header no-print">
        <button className="back-btn" onClick={() => navigate('/ops/pack-lists')}>← Back</button>
        <div className="detail-header-info">
          <h1>{packList.type === 'CC' ? 'Collective Culture' : 'Apple Ridge Farm'} Pack List</h1>
          <p>Week of {packList.week_date}</p>
        </div>
        <div className="detail-header-actions">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileRef}
            style={{ display: 'none' }}
            onChange={handlePhotoScan}
          />
          <button className="btn-scan" onClick={() => fileRef.current.click()} disabled={scanning}>
            {scanning ? 'Scanning...' : '📷 Scan Actuals'}
          </button>
          <button className="btn-secondary" onClick={handlePushToMarketDay} disabled={pushing}>
            {pushing ? 'Pushing...' : '→ Push to Market Day'}
          </button>
          <button className="btn-secondary" onClick={handlePrint}>🖨 Print Master</button>
          <button className="btn-secondary" onClick={() => navigate(`/ops/pack-lists/${id}/market-sheets`)}>📋 Market Sheets</button>
          {packList.status === 'in_progress' && (
            <button className="btn-secondary" onClick={markPrinted}>Mark Printed</button>
          )}
          {packList.status === 'printed' && (
            <button className="btn-primary" onClick={markComplete}>Mark Complete</button>
          )}
          {packList.status === 'complete' && (
            <span className="pill pill-green">Complete</span>
          )}
        </div>
      </div>

      <div className="page-body">
        {error && <div className="error-banner no-print">{error}</div>}

        <div className="print-header print-only">
          <h2>{packList.type === 'CC' ? 'Collective Culture' : 'Apple Ridge Farm'} — Master Pack List</h2>
          <p>Week of {packList.week_date} &nbsp;|&nbsp; Printed: {new Date().toLocaleDateString()}</p>
        </div>

        {groups.map(group => (
          <div key={group.label} className="pack-group">
            <div className="pack-group-header">{group.label}</div>
            <table className="data-table pack-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Suggested</th>
                  <th>Final Qty</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map(item => (
                  <tr key={item.id}>
                    <td className="td-name">{item.product_name}</td>
                    <td className="td-suggested">{item.qty_suggested || '—'}</td>
                    <td>
                      <input
                        type="number"
                        className="qty-input no-print"
                        value={item.qty_confirmed || ''}
                        onChange={e => handleLocalChange(item.id, 'qty_confirmed', e.target.value)}
                        onBlur={e => handleBlur(item.id, 'qty_confirmed', e.target.value)}
                        min="0"
                      />
                      <span className="print-only">{item.qty_confirmed || ''}</span>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="notes-input no-print"
                        value={item.notes || ''}
                        onChange={e => handleLocalChange(item.id, 'notes', e.target.value)}
                        onBlur={e => handleBlur(item.id, 'notes', e.target.value)}
                        placeholder="Notes..."
                      />
                      <span className="print-only">{item.notes || ''}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
